/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  CSSProperties,
  forwardRef,
  memo,
  NamedExoticComponent,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {areEqual} from 'react-window';
import Link from '@material-ui/core/Link';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {NavigationIcon} from '@ion/dls/src/icons/NavigationIcon';
import Breadcrumb from '../../components/breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '../../components/breadcrumbs/BreadcrumbUtils';
import ClickAwayListener from '../../components/listeners/ClickAwayListener';
import Hidden from '../../layout/Hidden';
import IonPortal from '../../render/IonPortal';
import {currentOnly, toggleThemeStyle} from '../../theme/helpers';
import {
  dlsAlt,
  dlsColor,
  dlsLayout,
  dlsShadow,
  dlsZIndex
} from '../../theme/theme';
import useThemeContext, {isLegacyTheme} from '../../theme/useThemeContext';
import {
  BoundaryMode,
  Color,
  CreateGeoFenceTypeEnum,
  IconSize
} from '../../types/types';
import {
  MAP_WIDGET_RIGHT,
  OMIT_CLICK_AWAY_LISTENER
} from '../constants';
import {removeActiveFence} from '../helpers/MapViewUtils';
import {useCreateGeofenceCard} from '../hooks/useCreateGeofenceCard';
import ToolbarPanelAnchor from '../toolbar/ToolbarPanelAnchor';
import {CreateGeofenceCardContext} from './CreateGeofenceCard';
import {EnterGeofenceLocation} from './EnterGeofenceLocation';
import {GeofencesList} from './GeofencesList';

type CreateGeofencesProps = GlobalGroupProps & {
  id?: string;
  map: __esri.Map;
  mapView: __esri.MapView;
  show: boolean;
  isPhone: boolean;
  handleViewAllClick?: Function;
  assetCategories?: string[];
  geofenceSave?: Function;
  savingGeofence?: boolean;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.SketchViewModel;
  };
  selectedMapPointRef?: {
    current: null | __esri.Layer;
  };
  editGeofenceGraphic?: __esri.Graphic;
  setEditGeofenceGraphic?: Function;
  MapControlsBreadCrumbs?: null | ReactElement | NamedExoticComponent;
  isShowViewAllOption?: boolean;
  closeGeoFencePanel?: Function;
  onClose?: () => void;
  isBoundaryCreation?: boolean;
  selectedDrawingTool?: CreateGeoFenceTypeEnum;
  boundaryMode?: BoundaryMode;
  onApplyBoundary?: Function;
  createPopupStyle?: CSSProperties;
  isAdminGeofence?: boolean;
  setIsGeofenceContentsOpen?: Function;
  useHereApi?: boolean;
  usePortal?: boolean;
  moveAllToolsToTop?: boolean;
  isFMTracking?: boolean;
  toggleShowBoundaryPanel?: Function;
  rootTitle?: string;
  topOffset?: number;
  geofenceTopOffset?: number;
  vicinityPosition?: {top: number, right: number} | null;
  closeRequest?: number;
};

type StyleProps = {
  moveAllToolsToTop?: boolean;
  isBoundaryCreation?: boolean;
  isAdminGeofence?: boolean;
  isFMTracking: boolean;
  isCreateCard?: boolean;
  isCreateCardSaveStep?: boolean;
  isVicinityCard?: boolean;
  vicinityPosition?: {top: number, right: number} | null;
};

type toggleBoundaryStyleProps = {
  opt1: number;
  opt2: string;
  opt3: string;
  isBoundaryCreation: boolean;
  moveAllToolsToTop: boolean;
};

// the current theme's create geofence and vicinity search card width ...
const CREATE_CARD_WIDTH = 360;

// the wrapper's width: the current theme's create and vicinity search cards
// are 360px (the create card's save step instead sizes to fit its form),
// while every other panel keeps its original fixed width ...
const getCreatePanelWidth = ({
  isAdminGeofence,
  isCreateCard,
  isCreateCardSaveStep,
  isVicinityCard
}: StyleProps) => {
  let width: number | string = isAdminGeofence ? (isLegacyTheme() ? 325 : 306) : 285;
  if (isCreateCard || isVicinityCard) {
    width = isCreateCardSaveStep ? 'auto' : CREATE_CARD_WIDTH;
  }
  return width;
};

// the legacy map panels' own shadow (Layers, Map Styles), mirrored to the
// left as well (its x offset negated) - a panel opening over the grid needs
// one on both of its sides ...
const getLegacyPanelShadow = (): string => {
  const shadow = dlsAlt('components', 'boxShadow', 'shadow4');
  return `${shadow}, -${shadow}`;
};

const toggleBoundaryStyle = ({
  opt1,
  opt2,
  opt3,
  isBoundaryCreation,
  moveAllToolsToTop
}: toggleBoundaryStyleProps) =>
  isBoundaryCreation ? opt1 : moveAllToolsToTop ? opt2 : opt3;

const useStyles = makeStyles(theme => {
  const {mobile} = dlsLayout('breakpoints');
  return {
    wrapper : {
      marginLeft   : 0,
      borderRadius : '4px 4px 0px 0px',
      maxWidth     : ({isAdminGeofence}: StyleProps) =>
        isLegacyTheme() ? (isAdminGeofence ? '100vh' : 285) : undefined,
      width      : getCreatePanelWidth,
      padding    : 0,
      background : toggleThemeStyle<string | undefined>(dlsColor('white'), undefined),
      // stands the legacy panel apart from the grid it opens over ...
      boxShadow  : () => (isLegacyTheme() ? getLegacyPanelShadow() : undefined),
      marginTop  : ({isBoundaryCreation}: {isBoundaryCreation: boolean}) =>
        (isLegacyTheme() && !isBoundaryCreation) ? '-8px' : '14px',
      top : ({isBoundaryCreation}: {isBoundaryCreation: boolean}) =>
        isBoundaryCreation ? 0 : (isLegacyTheme() ? '0px' : '96px'),
      right : ({isFMTracking, isBoundaryCreation, moveAllToolsToTop}: StyleProps) =>
        !isFMTracking ? toggleBoundaryStyle({opt1: 0, opt2: '135px', opt3: '50px', isBoundaryCreation, moveAllToolsToTop}) : MAP_WIDGET_RIGHT,
      position                       : 'absolute',
      zIndex                         : dlsZIndex('floatingActionButtonDisplay'),
      [theme.breakpoints.down('sm')] : {
        width     : '100%',
        maxWidth  : 'none',
        top       : 100,
        marginTop : 'inherit'
      }
    },
    legacyContainer : {
      [theme.breakpoints.up('md')] : {
        '&&' : {
          maxHeight : 400,
          overflowX : 'hidden',
          overflowY : 'auto'
        }
      }
    },
    legacyAdminContainer : {
      [theme.breakpoints.up('md')] : {
        '&&' : {
          maxHeight : 350
        }
      }
    },
    vicinityWrapper : {
      position  : 'fixed',
      marginTop : 0,
      top       : ({vicinityPosition}: StyleProps) => vicinityPosition?.top,
      right     : ({vicinityPosition}: StyleProps) => vicinityPosition?.right
    },
    // legacy and current are separate rules, applied by theme - the legacy
    // rule is plain again (given through toggleThemeStyle, a whole rule loses
    // its breakpoints and props-driven height, which left the legacy panel
    // unsized and scrolled); the current rule keeps its own form ...
    container : {
      borderRadius                   : '4px 4px 0px 0px',
      [theme.breakpoints.down('md')] : {
        padding : '0px 8px 36px 0px'
      },
      [theme.breakpoints.up('md')] : {
        maxHeight : 400,
        overflowX : 'hidden',
        overflowY : 'auto'
      },
      background : dlsColor('white')
    },
    containerCurrent : toggleThemeStyle({}, {
      width                        : 'auto',
      height                       : 'auto',
      display                      : 'flex',
      flexDirection                : 'column',
      boxShadow                    : dlsShadow(2),
      borderRadius                 : '12px',
      background                   : dlsColor('white'),
      borderTop                    : `2px solid ${dlsColor('pastelTeal')}`,
      [theme.breakpoints.up('md')] : {
        overflowX : 'hidden',
        overflowY : 'auto'
      }
    }),
    containerCreating : {
      borderRadius : ({selectedGeofenceType, isCreateCard, isVicinityCard}: {
        selectedGeofenceType?: CreateGeoFenceTypeEnum | null,
        isCreateCard?: boolean,
        isVicinityCard?: boolean
      }) => currentOnly(selectedGeofenceType && !isCreateCard && !isVicinityCard ? '0px 0px 12px 12px' : '12px'),
      borderTop     : toggleThemeStyle(undefined, 'none !important'),
      paddingBottom : ({selectedGeofenceType}: {selectedGeofenceType?: CreateGeoFenceTypeEnum | null}) =>
        currentOnly(selectedGeofenceType ? '0px' : '8px')
    },
    inactiveCrumb : {
      color    : dlsColor('white'),
      cursor   : 'pointer',
      fontSize : '12px'
    },
    activeCrumb : {
      color    : dlsColor('white'),
      fontSize : '12px'
    },
    breadcrumb : {
      padding       : 0,
      background    : dlsColor('grey10'),
      textTransform : 'uppercase',
      borderRadius  : '4px 4px 0px 0px',
      overflow      : 'hidden'
    },
    checkbox : {
      marginTop    : '10px',
      marginBottom : '10px',
      display      : 'block'
    },
    breadcrumbSeparator : {
      color    : dlsColor('white'),
      fontSize : '12px'
    }
  };
});

export const CreateGeofences = forwardRef(({
  id = 'map-create-geofences',
  map,
  mapView,
  show,
  isPhone,
  boundaryMode,
  handleViewAllClick,
  assetCategories,
  geofenceSave,
  accountMap,
  globalGroup,
  savingGeofence,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  editGeofenceGraphic,
  setEditGeofenceGraphic,
  MapControlsBreadCrumbs,
  isShowViewAllOption,
  closeGeoFencePanel,
  onClose,
  selectedDrawingTool,
  onApplyBoundary,
  toggleShowBoundaryPanel,
  createPopupStyle,
  setIsGeofenceContentsOpen,
  topOffset,
  geofenceTopOffset,
  vicinityPosition,
  rootTitle = translate('maps:geofences.rootTitle'),
  isBoundaryCreation = false,
  isAdminGeofence = false,
  useHereApi = false,
  usePortal = false,
  moveAllToolsToTop = false,
  isFMTracking = false,
  closeRequest = 0
}: CreateGeofencesProps, ref) => {
  const [
    selectedGeofenceType,
    setSelectedGeofenceType
  ] = useState<CreateGeoFenceTypeEnum | null>(selectedDrawingTool);

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const isCreateCard   = isCurrentTheme && !isBoundaryCreation && !editGeofenceGraphic;
  const isVicinityCard = isCurrentTheme && isBoundaryCreation && boundaryMode === BoundaryMode.Vicinity;

  const [isMain, setIsMain]                   = useState(false);
  const [isContents, setIsContents]           = useState(false);
  const [crumbs, setCrumbs]                   = useState([]);
  const [displayValues, setDisplayValues]     = useState([]);
  const [isBoundary, setIsBoundary]           = useState(false);
  const [boundary, setBoundary]               = useState('');
  const [clickNext, setClickNext]             = useState(false);
  const [mapClickHandler, setMapClickHandler] = useState(null);

  const shouldResetCrumbRef = useRef(false);

  const mapViewEl = document.querySelector('.esri-view-surface');
  const top = geofenceTopOffset || (isFMTracking ? topOffset : null);

  useEffect(() => {
    handleDefault(rootTitle);
    defineBreadCrumbChildren(crumbs);
  }, []);

  useEffect(() => {
    // Hook to reset the crumbs if panel is closed after saving
    if (!show && crumbs.length > 1) {
      selectCrumbs(0, crumbs);
    }
  }, [show]);

  useEffect(() => {
    if (isBoundaryCreation && selectedDrawingTool) {
      setIsContents(true);
      shouldResetCrumbRef.current = true;
      changeSelectedGeofenceType(selectedDrawingTool);
    }
  }, [isBoundaryCreation, selectedDrawingTool]);

  useEffect(() => {
    if (editGeofenceGraphic) {
      setIsMain(false);
      setIsContents(true);
      setSelectedGeofenceType(CreateGeoFenceTypeEnum.Draw);
    }
  }, [editGeofenceGraphic]);

  useEffect(() => {
    setIsGeofenceContentsOpen?.(isContents);
  }, [isContents]);

  useImperativeHandle(ref, () => ({
    handleBack
  }));

  const clearActiveDrawing = useCallback(() => {
    mapViewEl?.classList.remove('crosshair');
    mapViewEl?.classList.remove('crosshair-only');
    mapViewEl?.classList.add('default-cursor');
    removeActiveFence(
      map,
      [
        geoFencesGraphicsLayerRef,
        sketchViewModelRef,
        selectedMapPointRef
      ]
    );
    mapClickHandler?.remove();
  }, [mapClickHandler]);

  const handleBack = useCallback(() => {
    clearActiveDrawing();
    if (crumbs.length > 1) {
      removeBreadCrumb(crumbs);
    } else if (crumbs.length === 1) {
      setIsMain(true);
    }
  }, [clearActiveDrawing, crumbs]);

  const handleDefault = useCallback(crumb => {
    const temp = {label: crumb, method: function(param) { setIsMain(true); }};
    breadCrumbChildren(temp, crumbs);
    setIsMain(true);
  }, [crumbs]);

  const onFenceSelected = useCallback(crumb => {
    const temp = {
      label  : crumb,
      method : function(param) { setIsContents(param); }
    };
    breadCrumbChildren(temp, crumbs);
    setIsMain(false);
    setIsContents(true);
  }, [crumbs]);

  const onBoundarySelected = useCallback(crumb => {
    const temp = {
      label  : crumb,
      method : function(param) { setIsBoundary(param); }
    };
    breadCrumbChildren(temp, crumbs);
    setIsContents(false);
    setIsBoundary(true);
    setBoundary(crumb);
  }, [crumbs]);

  const breadCrumbChildren = useCallback((label, crumbs) => {
    let subCrumbs = crumbs;
    if (crumbs.length > 1 && shouldResetCrumbRef.current) {
      shouldResetCrumbRef.current = false;
      subCrumbs = [crumbs[0]];
    }
    subCrumbs.push(label);
    setCrumbs(subCrumbs);
    defineBreadCrumbChildren(subCrumbs);
  }, []);

  const onBack = useCallback(function() {
    handleBack();
    toggleShowBoundaryPanel?.();
  }, [handleBack, toggleShowBoundaryPanel]);

  // the request last handled - only a new one closes the panel ...
  const closeRequestRef = useRef(closeRequest);

  useEffect(() => {
    if (closeRequest !== closeRequestRef.current) {
      closeRequestRef.current = closeRequest;
      if (show) {
        onBack();
      }
    }
  }, [closeRequest]);

  const defineBreadCrumbChildren = useCallback(crumbs => {
    const displaycrumbs = [];
    crumbs.map((element, index) => {
      displaycrumbs.push(
        <Link key={index} style={{textDecoration: 'none'}}
          className={(crumbs.length - 1) === index
            ? classes.activeCrumb : classes.inactiveCrumb}
          onClick={(crumbs.length - 1) !== index ? onBack : null}>
          {index === 0
            ? element.label
            : translate(`maps:geofences.${element?.label?.toLowerCase()}`)}
        </Link>);
    });
    setDisplayValues(displaycrumbs);
  }, []);

  const selectCrumbs = useCallback((index, crumbs) => {
    const temp = crumbs;
    temp.forEach((element, i) => {
      if (i === 0 && (index === i)) {
        element.method();
        setSelectedGeofenceType(null);
      } else {
        if (i === index) {
          element.method(true);
        } else if (i !== 0) {
          element.method(false);
        }
      }
    });
    temp.splice(index + 1, temp.length);
    setCrumbs(temp);
    defineBreadCrumbChildren(crumbs);
    if (setEditGeofenceGraphic) {
      setEditGeofenceGraphic(null);
    }
    if (isBoundaryCreation) {
      closeGeoFencePanel();
    }
  }, [isBoundaryCreation]);

  const removeBreadCrumb = useCallback(crumbs => {
    const temp = crumbs;
    const removed = temp.splice(temp.length - 1, temp.length);
    setCrumbs(temp);
    if (removed.length > 0) {
      removed[0].method(false);
      if (crumbs.length === 1) {
        setSelectedGeofenceType(null);
      }
    }
    if (temp.length > 0) {
      temp[(temp.length - 1)].method(true);
    }
    defineBreadCrumbChildren(crumbs);
    if (isBoundaryCreation) {
      closeGeoFencePanel();
    }
  }, [isBoundaryCreation]); ;

  const changeSelectedGeofenceType = useCallback((value: CreateGeoFenceTypeEnum) => {
    setClickNext(false);
    onFenceSelected(value);
    setSelectedGeofenceType(value);
  }, []);

  const draw = useCallback(() => {
    setClickNext(true);
  }, []);

  // the current theme's create card state - after the handlers it uses, and
  // ahead of the styles it sizes ...
  const {isCreateCardSaveStep, createCardContext} = useCreateGeofenceCard({
    isCreateCard,
    show,
    selectedGeofenceType,
    setSelectedGeofenceType,
    changeSelectedGeofenceType,
    crumbs,
    selectCrumbs,
    clearActiveDrawing,
    setClickNext,
    onClosePanel : closeGeoFencePanel ?? onClose
  });

  const classes = useStyles({
    isBoundaryCreation,
    isAdminGeofence,
    moveAllToolsToTop,
    isFMTracking,
    selectedGeofenceType,
    isCreateCard,
    isCreateCardSaveStep,
    isVicinityCard,
    vicinityPosition : isVicinityCard ? vicinityPosition : null
  });

  const externalCrumb: any = useMemo(() => (
    boundaryMode === BoundaryMode.Vicinity ? defineBreadcrumbs({
      crumbs : [{
        label   : translate('maps:contextMenu.vicinitySearch'),
        click   : true,
        onClick : onBack
      }
      ],
      classes : {
        activeCrumb : classes.activeCrumb
      }
    }) : null
  ), [boundaryMode, onBack]);

  /**
   * @private
   */
  const shouldClose = useCallback((target, canShow, crumbsList) => {
    if (target) {
      const targetClassName = target?.getAttribute?.('class') || '';
      return canShow &&
           !targetClassName.includes(OMIT_CLICK_AWAY_LISTENER) &&
           !target?.innerHTML?.includes(OMIT_CLICK_AWAY_LISTENER) &&
           crumbsList?.length <= 1;
    }
    return true;
  }, []);

  const onClickAway = useCallback(function({target}) {
    if (shouldClose(target, show, crumbs)) {
      onClose?.();
    }
  }, [show, crumbs]);

  // the vicinity search card's own content only mounts after the panel opens
  // (isContents is set in an effect) and unmounts before the panel's `show`
  // turns off (that arrives through a separate map widget root) - its
  // wrapper stays hidden in between, instead of flashing as an empty white
  // card ...
  const isEmptyVicinityCard = isVicinityCard && !isContents;

  // the current theme's vicinity card (placed by the map's action buttons)
  // opens on every screen size, as its other widget panels do - not inside
  // the mobile bottom sheet ...
  const cardBreakpoint = isVicinityCard && vicinityPosition ? '' : (isPhone ? 'mobile' : 'desktop');

  const createPanelStyle = useMemo(() => ({
    display : show && !isEmptyVicinityCard ? isPhone? 'contents' : 'inherit' : 'none',
    ...createPopupStyle
  }), [createPopupStyle, show, isPhone, isEmptyVicinityCard]);

  /** This constant will show main boundary/geofence panel only
   * for geofence panel (desktop & mobile) and boundary (mobile only))
   */
  const isShowMainGeofencePanel = useMemo(() => {
    if (isMain) {
      if (isPhone) {
        return !selectedGeofenceType;
      } else {
        return !selectedGeofenceType && !isBoundaryCreation;
      }
    }
  }, [isPhone, selectedGeofenceType, isBoundaryCreation, isMain]);

  const component = (
    show && (
      <ClickAwayListener onClickAway={onClickAway}
        mountListener>
        <div style={createPanelStyle}
          className={classnames(classes.wrapper, isVicinityCard && vicinityPosition && classes.vicinityWrapper)}>
          {
            crumbs?.length > 1 && !isCreateCard && !isVicinityCard && (
              <div className={classes.breadcrumb}>
                <Breadcrumb style={{height: isCurrentTheme ? '28px' : '26px'}}
                  icon={(
                    <NavigationIcon direction='back'
                      color={Color.white}
                      {...(isCurrentTheme ? {size: IconSize.largeMedium} : {})} />
                  )}
                  className={classes.breadcrumb}
                  color={Color.white}
                  iconClick={onBack}
                  separator={(
                    <span className={classes.breadcrumbSeparator}>
                    -
                    </span>
                  )}
                  height={isPhone && '45px'}>
                  {externalCrumb ?? displayValues}
                </Breadcrumb>
              </div>
            )
          }
          <div className={classnames(isCurrentTheme ? classes.containerCurrent : classes.container, {
            [classes.containerCreating]    : !editGeofenceGraphic,
            [classes.legacyContainer]      : isLegacyTheme(),
            [classes.legacyAdminContainer] : isLegacyTheme() && isAdminGeofence
          })}>
            {isShowMainGeofencePanel && !isCreateCard && (
              <GeofencesList changeSelectedGeofenceType={changeSelectedGeofenceType}
                isBoundaryCreation={isBoundaryCreation}
                handleViewAllClick={handleViewAllClick}
                MapControlsBreadCrumbs={MapControlsBreadCrumbs}
                isShowViewAllOption={isShowViewAllOption}
                isPhone={isPhone}
                isAdminGeofence={isAdminGeofence} />
            )}
            {isContents && (
              <EnterGeofenceLocation selectedGeofenceType={selectedGeofenceType}
                isFMTracking={isFMTracking}
                accountMap={accountMap}
                globalGroup={globalGroup}
                useHereApi={useHereApi}
                boundaryMode={boundaryMode}
                onBoundarySelected={onBoundarySelected}
                isBoundary={isBoundary || isBoundaryCreation}
                onApplyBoundary={onApplyBoundary}
                toggleShowBoundaryPanel={toggleShowBoundaryPanel}
                isVisible={show}
                mapView={mapView}
                map={map}
                draw={draw}
                clickNext={clickNext}
                assetCategories={assetCategories}
                closeGeoFencePanel={closeGeoFencePanel}
                geofenceSave={geofenceSave}
                selectCrumbs={selectCrumbs}
                crumbs={crumbs}
                handleBack={handleBack}
                savingGeofence={savingGeofence}
                mapViewEl={mapViewEl}
                editGeofenceGraphic={editGeofenceGraphic}
                geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
                sketchViewModelRef={sketchViewModelRef}
                selectedMapPointRef={selectedMapPointRef}
                mapClickHandler={mapClickHandler}
                setMapClickHandler={setMapClickHandler}
                isPhone={isPhone} />
            )}
            {selectedGeofenceType && isBoundary && boundary === `${translate('maps:geofences.municipalities')}` && (
              <></>
            )}
            {selectedGeofenceType && isBoundary && boundary === `${translate('maps:geofences.area')}` && (
              <></>
            )}
          </div>
        </div>
      </ClickAwayListener>
    ));

  return (
    <CreateGeofenceCardContext.Provider value={createCardContext}>
      <Hidden show={cardBreakpoint}>
        {usePortal ? (
          <IonPortal id={id} top={top} show={show}>
            <ToolbarPanelAnchor enabled={!isCurrentTheme && !isBoundaryCreation && moveAllToolsToTop} show={show}>
              {component}
            </ToolbarPanelAnchor>
          </IonPortal>
        ) : (
          component
        )}
      </Hidden>
    </CreateGeofenceCardContext.Provider>
  );
});

export default memo(CreateGeofences, areEqual);
